/**
 * Ramp Embed - Main Orchestration
 */
import {
  decodeJwt,
  exchangeInitToken,
  exchangeSessionToken,
  validateInitToken,
} from "./api";
import { logger } from "./logger";
import { mountMarqeta } from "./renderers/marqeta";
import { mountStripe } from "./renderers/stripe";
import { generateNonce, loadStripeInstance } from "./stripe";
import {
  type EmbedConfig,
  EmbedError,
  type EmbedEvent,
  type EmbedInitMessage,
  type EmbedInitTokenPayload,
  type EmbedState,
  type ExchangeInitTokenResponse,
  type ExchangeSessionTokenResponse,
} from "./types";
import { type Stripe } from "@stripe/stripe-js";

interface EmbedOptions {
  container: HTMLElement;
  config: EmbedConfig;
}

export function createEmbed({ container, config }: EmbedOptions) {
  let state: EmbedState = "idle";
  let parentOrigin: string | null = null;
  let jti: string | null = null;

  function setState(newState: EmbedState) {
    state = newState;
    render();
  }

  function sendToParent(event: EmbedEvent) {
    if (window.parent !== window && parentOrigin) {
      window.parent.postMessage(event, parentOrigin);
    }
  }

  function handleMessage(event: MessageEvent) {
    if (event.source !== window.parent) {
      return;
    }

    const data = event.data as Partial<EmbedInitMessage>;
    if (data.type !== "ramp.embed.init" || !data.token) {
      return;
    }

    if (state !== "idle") {
      return;
    }

    processToken(data.token, event.origin);
  }

  async function processToken(token: string, origin: string) {
    const startTime = performance.now();

    try {
      const payload = decodeJwt<EmbedInitTokenPayload>(token);
      validateInitToken(payload, origin);

      jti = payload.jti;
      parentOrigin = origin;

      setState("exchanging");
      const session = await exchangeInitToken({
        embed_init_token: token,
        parent_origin: origin,
      });

      setState("loading_card");
      const { issuerData, nonce, stripe } = await getIssuerToken(session);

      setState("displaying");
      await mountCard(issuerData, nonce, stripe);

      const duration = Math.round(performance.now() - startTime);
      logger.info("embed_ready", {
        jti,
        issuer: issuerData.card_issuer,
        duration_ms: duration,
      });

      sendToParent({ type: "ramp.embed.ready" });
    } catch (err) {
      handleError(err);
    }
  }

  async function getIssuerToken(session: ExchangeInitTokenResponse): Promise<{
    issuerData: ExchangeSessionTokenResponse;
    nonce: string | null;
    stripe: Stripe | null;
  }> {
    if (session.card_issuer === "STRIPE") {
      if (!session.stripe_card_id) {
        throw new EmbedError("INVALID_TOKEN", "Missing stripe_card_id");
      }

      const stripe = await loadStripeInstance(
        config,
        session.issuing_product,
        session.stripe_account ?? undefined
      );
      const nonce = await generateNonce(stripe, session.stripe_card_id);

      const issuerData = await exchangeSessionToken({
        embed_session_token: session.session_token,
        nonce,
      });

      return { issuerData, nonce, stripe };
    }

    const issuerData = await exchangeSessionToken({
      embed_session_token: session.session_token,
      nonce: null,
    });

    return { issuerData, nonce: null, stripe: null };
  }

  async function mountCard(
    issuerData: ExchangeSessionTokenResponse,
    nonce: string | null,
    stripe: Stripe | null
  ) {
    const cardContainer = document.createElement("div");
    cardContainer.id = "card-container";
    container.innerHTML = "";
    container.appendChild(cardContainer);

    const onCopied = (field: "number" | "expiry" | "cvv" | "pin") => {
      sendToParent({ type: "ramp.embed.copied", field });
    };

    if (issuerData.card_issuer === "STRIPE") {
      await mountStripe({
        container: cardContainer,
        issuerData,
        nonce: nonce!,
        stripe: stripe!,
        jti: jti!,
        onCopied,
      });
    } else {
      await mountMarqeta({
        container: cardContainer,
        issuerData,
        config,
        jti: jti!,
        onCopied,
      });
    }
  }

  function handleError(err: unknown) {
    const embedError =
      err instanceof EmbedError
        ? err
        : new EmbedError(
            "UNKNOWN",
            err instanceof Error ? err.message : "Unknown error"
          );

    logger.error("embed_error", {
      jti,
      code: embedError.code,
      message: embedError.message,
      recoverable: embedError.recoverable,
    });

    setState("error");
    sendToParent({
      type: "ramp.embed.error",
      code: embedError.code,
      message: embedError.message,
    });

    container.innerHTML = `
      <div class="embed-error">
        <div class="embed-error-title">Unable to load card</div>
        <div class="embed-error-message">${escapeHtml(embedError.message)}</div>
      </div>
    `;
  }

  function render() {
    if (state === "idle") {
      container.innerHTML = `<div class="embed-loading">Waiting for initialization</div>`;
    } else if (state === "exchanging" || state === "loading_card") {
      container.innerHTML = `<div class="embed-loading">Loading card details</div>`;
    }
  }

  function start() {
    window.addEventListener("message", handleMessage);
    render();
  }

  function destroy() {
    window.removeEventListener("message", handleMessage);
  }

  return { start, destroy };
}

function escapeHtml(text: string): string {
  const div = document.createElement("div");
  div.textContent = text;
  return div.innerHTML;
}
